import { useState, useEffect } from 'react';
import { clamp, roundHalfToNearest, parseOrNull, isPermissiveNumeric } from '../../../utils/number.esm.js';

function useRangeSliderValues({ min, max, defaultValue, value, onChange, }) {
    var _a, _b, _c, _d;
    const isControlled = value !== undefined;
    const initialLower = clamp(roundHalfToNearest((_b = (_a = value === null || value === void 0 ? void 0 : value[0]) !== null && _a !== void 0 ? _a : defaultValue === null || defaultValue === void 0 ? void 0 : defaultValue[0]) !== null && _b !== void 0 ? _b : min), min, max);
    const initialUpper = clamp(roundHalfToNearest((_d = (_c = value === null || value === void 0 ? void 0 : value[1]) !== null && _c !== void 0 ? _c : defaultValue === null || defaultValue === void 0 ? void 0 : defaultValue[1]) !== null && _d !== void 0 ? _d : max), min, max);
    const [rangeValues, setRangeValues] = useState([
        initialLower,
        initialUpper,
    ]);
    const [inputTexts, setInputTexts] = useState([
        String(initialLower),
        String(initialUpper),
    ]);
    useEffect(() => {
        if (isControlled &&
            value &&
            (value[0] !== rangeValues[0] || value[1] !== rangeValues[1])) {
            const clampedLower = clamp(value[0], min, max);
            const clampedUpper = clamp(value[1], min, max);
            const orderedLower = Math.min(clampedLower, clampedUpper);
            const orderedUpper = Math.max(clampedLower, clampedUpper);
            setRangeValues([orderedLower, orderedUpper]);
            setInputTexts([String(orderedLower), String(orderedUpper)]);
        }
    }, [value, isControlled, min, max, rangeValues]);
    const handleInputChange = (index) => (e) => {
        const s = e.target.value;
        if (s === "" || s === "-") {
            setInputTexts((prev) => {
                const next = [prev[0], prev[1]];
                next[index] = s;
                return next;
            });
            if (!isControlled) {
                setRangeValues((prev) => {
                    const next = [prev[0], prev[1]];
                    next[index] = min;
                    return next;
                });
            }
            return;
        }
        if (!isPermissiveNumeric(s))
            return;
        setInputTexts((prev) => {
            const next = [prev[0], prev[1]];
            next[index] = s;
            return next;
        });
        const parsed = parseOrNull(s);
        if (parsed == null)
            return;
        const clamped = clamp(parsed, min, max);
        const otherValue = rangeValues[index === 0 ? 1 : 0];
        const crossing = (index === 0 && clamped > otherValue) ||
            (index === 1 && clamped < otherValue);
        if (crossing)
            return;
        if (!isControlled) {
            setRangeValues((prev) => {
                const next = [prev[0], prev[1]];
                next[index] = clamped;
                return next;
            });
        }
        const emitted = index === 0 ? [clamped, rangeValues[1]] : [rangeValues[0], clamped];
        onChange === null || onChange === void 0 ? void 0 : onChange(emitted);
    };
    const handleInputBlur = (index) => () => {
        const raw = inputTexts[index];
        const parsed = parseOrNull(raw);
        if (parsed == null) {
            setInputTexts((prev) => {
                const next = [prev[0], prev[1]];
                next[index] = String(rangeValues[index]);
                return next;
            });
            return;
        }
        let committed = clamp(roundHalfToNearest(parsed), min, max);
        const otherValue = rangeValues[index === 0 ? 1 : 0];
        if (index === 0 && committed > otherValue) {
            committed = otherValue;
        }
        else if (index === 1 && committed < otherValue) {
            committed = otherValue;
        }
        if (!isControlled) {
            setRangeValues((prev) => {
                const next = [prev[0], prev[1]];
                next[index] = committed;
                return next;
            });
        }
        setInputTexts((prev) => {
            const next = [prev[0], prev[1]];
            next[index] = String(committed);
            return next;
        });
        const emitted = index === 0 ? [committed, rangeValues[1]] : [rangeValues[0], committed];
        onChange === null || onChange === void 0 ? void 0 : onChange(emitted);
    };
    const handleSliderChange = (vals) => {
        const lowerRaw = clamp(vals[0], min, max);
        const upperRaw = clamp(vals[1], min, max);
        const orderedLower = Math.min(lowerRaw, upperRaw);
        const orderedUpper = Math.max(lowerRaw, upperRaw);
        const nextTuple = [orderedLower, orderedUpper];
        if (!isControlled) {
            setRangeValues(nextTuple);
            setInputTexts([String(orderedLower), String(orderedUpper)]);
        }
        else {
            onChange === null || onChange === void 0 ? void 0 : onChange(nextTuple);
        }
    };
    return {
        rangeValues,
        inputTexts,
        handleInputChange,
        handleInputBlur,
        handleSliderChange,
    };
}

export { useRangeSliderValues };
